import React from "react";
import { useTranslation } from "react-i18next";
import { MenuList } from "@mui/material";
import { HeaderSubtitle } from "../Common/HeaderSubtitle";
import { HeaderItem } from "../Common/HeaderItem";
import {
  UserCircle,
  UsersThree,
  Keyhole,
  ChartBar,
  ReadCvLogo,
  Password,
  UserSwitch,
  Fingerprint,
  Pen,
  DoorOpen,
} from "@phosphor-icons/react";
import { headerActions } from "../utils";
import actions from "../../../actions";
import { useDispatch } from "react-redux";
import { ExperimentsDrawerGroup } from "../../Experiments/ExperimentsDrawerGroup";

interface DrawerMainContentProps {
  user: any;
  onMenuItemClick: (clickHandler: () => void) => void;
  onNavigateToProfile: () => void;
  onNavigateToClientProfile: () => void;
  onNavigateToCareTeam: () => void;
  onNavigateToDataPrivacy: () => void;
  onNavigateToAssessments: () => void;
  onNavigateToDocuments: () => void;
  onSwitchAccountsClick: (event: React.MouseEvent<HTMLElement>) => void;
  logout: () => void;
  isDocumentSigner: boolean;
  hasMultipleClients: boolean;
  customerDetails?: any;
}

export const DrawerMainContent: React.FC<DrawerMainContentProps> = ({
  onMenuItemClick,
  onNavigateToProfile,
  onNavigateToClientProfile,
  onNavigateToCareTeam,
  onNavigateToDataPrivacy,
  onNavigateToAssessments,
  onNavigateToDocuments,
  onSwitchAccountsClick,
  isDocumentSigner,
  hasMultipleClients,
  customerDetails,
  user,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["clientHeader"]);
  const clientName = customerDetails?.name || t("clientHeader:menu.client");

  return (
    <>
      <HeaderSubtitle>{t("clientHeader:menu.account")}</HeaderSubtitle>

      <MenuList sx={{ padding: "0px" }}>
        <HeaderItem
          icon={UserCircle}
          onClick={onNavigateToProfile}
          text={t("clientHeader:menu.myProfile")}
        />

        <HeaderItem
          icon={UserCircle}
          onClick={onNavigateToClientProfile}
          text={t("clientHeader:menu.clientProfile", { name: clientName })}
        />

        <HeaderItem
          icon={UsersThree}
          onClick={onNavigateToCareTeam}
          text={t("clientHeader:menu.careTeam", { name: clientName })}
        />

        {isDocumentSigner && (
          <HeaderItem
            icon={Keyhole}
            onClick={onNavigateToDataPrivacy}
            text={t("clientHeader:menu.dataPrivacy")}
          />
        )}

        <HeaderItem
          icon={ChartBar}
          onClick={onNavigateToAssessments}
          text={t("clientHeader:menu.assessments")}
        />

        <HeaderItem
          icon={ReadCvLogo}
          onClick={onNavigateToDocuments}
          text={t("clientHeader:menu.documents")}
        />

        <ExperimentsDrawerGroup
          userId={user?.id}
          audience="client"
          onNavigate={() => onMenuItemClick(() => {})}
        />
      </MenuList>

      <HeaderSubtitle>{t("clientHeader:menu.supportTools")}</HeaderSubtitle>

      <MenuList sx={{ padding: "0px" }}>
        <HeaderItem
          icon={Password}
          onClick={() => onMenuItemClick(headerActions.navigation.resetPassword)}
          text={t("clientHeader:menu.resetPassword")}
        />

        {hasMultipleClients && (
          <HeaderItem
            icon={UserSwitch}
            onClick={onSwitchAccountsClick}
            text={t("clientHeader:menu.switchAccounts")}
          />
        )}

        <HeaderItem
          icon={Fingerprint}
          onClick={() => onMenuItemClick(headerActions.external.privacyPolicy)}
          isExternalLink
          text={t("clientHeader:menu.privacyPolicy")}
        />

        <HeaderItem
          icon={Pen}
          onClick={() => onMenuItemClick(headerActions.external.termsOfUse)}
          isExternalLink
          text={t("clientHeader:menu.termsOfUse")}
        />

        <HeaderItem
          icon={DoorOpen}
          onClick={() => onMenuItemClick(() => dispatch(actions.signOut(true)))}
          text={t("clientHeader:menu.logout")}
        />
      </MenuList>
    </>
  );
};
